import { useState, useEffect, useCallback, type DragEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Upload, Trash2, MoveRight, FolderPlus,
  FileUp, CheckCircle, AlertCircle, X, Share2, Users, UserPlus, ChevronDown, LayoutGrid, List,
} from 'lucide-react'
import { getDocuments, getFolders, getProjects, createFolder, deleteFolder, deleteDocument, getUploadUrl, confirmUpload, getPermissions, revokePermission, checkManagerAccess } from '../../services/api'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import type { Folder, Permission } from '../../types'
import { granteeLabel, granteeInitial } from '../../utils/collaborators'
import FolderTree from '../../components/documents/FolderTree'
import type { FolderAction } from '../../components/documents/FolderTree'
import DocumentCard from '../../components/documents/DocumentCard'
import DocumentGrid from '../../components/documents/DocumentGrid'
import MoveDocumentModal from '../../components/documents/MoveDocumentModal'
import ShareModal from '../../components/documents/ShareModal'
import CollaboratorPermissionsModal from '../../components/documents/CollaboratorPermissionsModal'
import FolderListTabs, { type FolderTab } from '../../components/documents/FolderListTabs'
import RelatedJournalsPanel from '../../components/documents/RelatedJournalsPanel'
import CollaborationPanel from '../../components/collaboration/CollaborationPanel'
import FolderNotesList from '../../components/documents/FolderNotesList'
import FolderConnectionsGraph from '../../components/documents/FolderConnectionsGraph'
import FolderAIAnalysisPanel from '../../components/documents/FolderAIAnalysisPanel'
import FolderPriorArtPanel from '../../components/documents/FolderPriorArtPanel'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import { useSetSpine } from '../../components/spine'

interface InlineUpload {
  file: File
  status: 'pending' | 'uploading' | 'complete' | 'error'
  progress: number
  error?: string
}

export default function FolderView() {
  const { projectId, folderId } = useParams<{ projectId: string; folderId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [docView, setDocView] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem('innovateflow:doc-view:folder') as 'list' | 'grid') || 'list' } catch { return 'list' }
  })
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set())
  const [showBulkMove, setShowBulkMove] = useState(false)
  const [showShareFolder, setShowShareFolder] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<import('../../types').Folder | null>(null)
  const [deleteFolderError, setDeleteFolderError] = useState('')
  const [permModalCollaborator, setPermModalCollaborator] = useState<{ grantee_id: string; grantee_email: string } | null>(null)
  const [isManager, setIsManager] = useState(false)
  const [collabPanelOpen, setCollabPanelOpen] = useState(true)

  // Fetch project for breadcrumb name
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
  })
  const project = projects?.find((p) => p.project_id === projectId)

  // Fetch all folders for tree + breadcrumb path
  const { data: folders } = useQuery({
    queryKey: ['folders', projectId],
    queryFn: () => getFolders(projectId!),
    enabled: !!projectId,
  })
  const currentFolder = folders?.find((f) => f.folder_id === folderId)

  // Fetch documents for this specific folder.
  // BUG-3221-FIX-S2D: includeOrphaned=true so s3_orphaned docs that live in
  // this folder show up with the "Missing file" badge (DocumentCard renders
  // it). FolderView has no toggle — the smaller list makes a per-folder hide
  // unnecessary, and surfacing orphans here lets inventors find them in
  // context.
  const { data: documents, isLoading } = useQuery({
    queryKey: ['documents', projectId, folderId, 'with-orphans'],
    queryFn: () => getDocuments(projectId!, folderId, true),
    enabled: !!projectId,
  })

  // Fetch folder-level and project-level permissions
  const { data: folderCollaborators } = useQuery({
    queryKey: ['permissions', folderId],
    queryFn: () => getPermissions(folderId!),
    enabled: !!folderId,
  })

  const { data: projectCollaborators } = useQuery({
    queryKey: ['permissions', projectId],
    queryFn: () => getPermissions(projectId!),
    enabled: !!projectId,
  })

  // Check if current user has manager-level access
  useEffect(() => {
    if (projectId && user?.id) {
      checkManagerAccess(projectId, user.id).then(setIsManager)
    }
  }, [projectId, user?.id])

  const removeCollaboratorMutation = useMutation({
    mutationFn: (granteeId: string) => revokePermission(folderId!, granteeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['permissions', folderId] }),
  })

  // Merge folder + project collaborators for display
  const folderGranteeIds = new Set(folderCollaborators?.map((c) => c.grantee_id) || [])
  const inheritedCollaborators = (projectCollaborators || []).filter((c) => !folderGranteeIds.has(c.grantee_id))

  // Build breadcrumb path by walking up folder hierarchy
  const breadcrumbFolders: Folder[] = []
  if (folders && folderId) {
    let cur = folders.find((f) => f.folder_id === folderId)
    while (cur) {
      breadcrumbFolders.unshift(cur)
      if (cur.parent_folder_id && cur.parent_folder_id !== 'root') {
        const parent = folders.find((f) => f.folder_id === cur!.parent_folder_id)
        if (!parent) break
        cur = parent
      } else {
        break
      }
    }
  }

  // ---------- Inline drag-and-drop upload ----------
  const [inlineFiles, setInlineFiles] = useState<InlineUpload[]>([])
  const [dragOver, setDragOver] = useState(false)

  const inlineUploadMutation = useMutation({
    mutationFn: async (uf: InlineUpload) => {
      const contentType = uf.file.type || 'application/octet-stream'
      const { upload_url, document_id, s3_key, required_headers, upload_id } = await getUploadUrl(projectId!, uf.file.name, contentType, folderId)
      await axios.put(upload_url, uf.file, {
        headers: { 'Content-Type': contentType, ...required_headers },
        onUploadProgress: (e) => {
          if (e.total) {
            const progress = Math.round((e.loaded / e.total) * 100)
            setInlineFiles((prev) => prev.map((f) => (f.file === uf.file ? { ...f, progress } : f)))
          }
        },
      })
      await confirmUpload({
        upload_id,
        document_id,
        project_id: projectId!,
        s3_key,
        title: uf.file.name,
        filename: uf.file.name,
        content_type: contentType,
        folder_id: folderId,
      })
      return document_id
    },
    onSuccess: (_docId, uf) => {
      setInlineFiles((prev) => prev.map((f) => (f.file === uf.file ? { ...f, status: 'complete', progress: 100 } : f)))
      queryClient.invalidateQueries({ queryKey: ['documents', projectId, folderId] })
    },
    onError: (err, uf) => {
      setInlineFiles((prev) =>
        prev.map((f) =>
          f.file === uf.file ? { ...f, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' } : f
        )
      )
    },
  })

  const handleInlineDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      if (!e.dataTransfer.files.length) return
      const newFiles: InlineUpload[] = Array.from(e.dataTransfer.files).map((file) => ({
        file,
        status: 'uploading' as const,
        progress: 0,
      }))
      setInlineFiles((prev) => [...prev, ...newFiles])
      newFiles.forEach((uf) => inlineUploadMutation.mutate(uf))
    },
    [projectId, folderId, inlineUploadMutation]
  )

  const handleInlineFileInput = useCallback(
    (fileList: FileList) => {
      const newFiles: InlineUpload[] = Array.from(fileList).map((file) => ({
        file,
        status: 'uploading' as const,
        progress: 0,
      }))
      setInlineFiles((prev) => [...prev, ...newFiles])
      newFiles.forEach((uf) => inlineUploadMutation.mutate(uf))
    },
    [inlineUploadMutation]
  )

  // ---------- Subfolder creation ----------
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [createFolderError, setCreateFolderError] = useState('')

  const createFolderMutation = useMutation({
    mutationFn: (data: { name: string; parent_folder_id: string | null }) => createFolder(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders', projectId] })
      setShowCreateFolder(false)
      setNewFolderName('')
      setCreateFolderError('')
    },
    // BUG-126: without onError the create-subfolder modal sat open silently on
    // failure. Surface the real error inside the modal.
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setCreateFolderError(err.response?.data?.error || err.message || 'Failed to create folder')
    },
  })

  const deleteDocsMutation = useMutation({
    mutationFn: async (docIds: string[]) => {
      for (const id of docIds) await deleteDocument(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', projectId, folderId] })
      setSelectedDocs(new Set())
      setShowDeleteConfirm(false)
      setDeleteError('')
    },
    onError: (err: Error) => {
      setDeleteError(err.message || 'Failed to delete documents')
    },
  })

  const deleteFolderMutation = useMutation({
    mutationFn: (fid: string) => deleteFolder(projectId!, fid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders', projectId] })
      setDeleteFolderTarget(null)
      setDeleteFolderError('')
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setDeleteFolderError(err.response?.data?.error || err.message || 'Failed to delete folder')
    },
  })

  const handleFolderAction = (action: FolderAction) => {
    switch (action.type) {
      case 'create':
        setNewFolderName('')
        setCreateFolderError('')
        setShowCreateFolder(true)
        break
      case 'delete':
        if (action.folder) {
          setDeleteFolderTarget(action.folder)
          setDeleteFolderError('')
        }
        break
      default:
        // Navigate to project view for rename/move
        navigate(`/project/${projectId}`)
        break
    }
  }

  const toggleSelect = (docId: string) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev)
      if (next.has(docId)) next.delete(docId)
      else next.add(docId)
      return next
    })
  }

  // The spine (Story #5648 build v2). A folder is a place inside an invention,
  // not a zoom level of its own, so the rail stays at Invention and the crumb —
  // the ONE crumb, rendered by the layout — carries the folder path this page
  // used to draw for itself.
  useSetSpine(project ? {
    level: 'invention',
    levels: ['invention', 'my-inventions'],
    links: { 'my-inventions': '/projects' },
    youreAt: [project.name, ...breadcrumbFolders.map((f) => f.name)],
    zoomIn: { title: 'Invention is the innermost scope — a folder is a place inside it, not a scope of its own' },
    zoomOut: { to: `/project/${projectId}`, title: 'Zoom out — Invention' },
    crumb: [
      { label: 'Personal', to: '/' },
      { label: 'My inventions', to: '/projects' },
      { label: project.name, to: `/project/${projectId}` },
      // Every folder above this one is a step the reader can walk back to; the
      // one they are in is the bold end of the crumb.
      ...breadcrumbFolders.map((f, i) =>
        i < breadcrumbFolders.length - 1
          ? { label: f.name, to: `/project/${projectId}/folder/${f.folder_id}` }
          : f.name),
    ],
  } : null)

  if (isLoading) return <LoadingSkeleton rows={4} type="list" />

  return (
    <div>
      {/* The single crumb comes from the spine spec this page declares, rendered
          by the layout. The page-level folder strip it replaces is gone. */}
      <PageHeader
        title={currentFolder?.name || 'Folder'}
        description="A folder organises documents within a project. Upload files here, or open a document to analyse it."
        primaryAction={
          <label className="flex items-center gap-1.5 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800 cursor-pointer transition-colors">
            <FileUp size={14} /> Add Files
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => e.target.files && handleInlineFileInput(e.target.files)}
            />
          </label>
        }
        secondaryActions={
          <button
            onClick={() => setShowShareFolder(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-sky-200 rounded-lg hover:bg-white/50 transition-colors"
          >
            <Share2 size={14} /> Share Folder
          </button>
        }
      />

      <div className="flex gap-6">
        {/* Folder tree sidebar */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="light-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Folders</h3>
              <button
                onClick={() => {
                  setNewFolderName('')
                  setCreateFolderError('')
                  setShowCreateFolder(true)
                }}
                className="p-1 text-gray-500 hover:text-iflow-600 transition-colors"
                title="New subfolder"
              >
                <FolderPlus size={16} />
              </button>
            </div>
            <FolderTree
              folders={folders || []}
              projectId={projectId!}
              currentFolderId={folderId || null}
              onAction={handleFolderAction}
            />
          </div>

          {/* Collaborators panel */}
          <div className="light-card p-4 mt-3">
            <button
              onClick={() => setCollabPanelOpen(!collabPanelOpen)}
              className="flex items-center justify-between w-full mb-1"
            >
              <div className="flex items-center gap-2">
                <Users size={14} className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-800">Collaborators</h3>
              </div>
              <div className="flex items-center gap-1">
                {isManager && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setShowShareFolder(true) }}
                    className="p-1 text-gray-500 hover:text-iflow-600 transition-colors"
                    title="Add collaborator"
                  >
                    <UserPlus size={14} />
                  </span>
                )}
                <ChevronDown size={14} className={`text-gray-500 transition-transform ${collabPanelOpen ? '' : '-rotate-90'}`} />
              </div>
            </button>
            {collabPanelOpen && (
              <div className="max-h-48 overflow-y-auto space-y-1.5 mt-2">
                {/* Explicit folder collaborators */}
                {folderCollaborators?.map((perm: Permission) => (
                  <div key={perm.grantee_id} className="flex items-center justify-between group/collab">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-5 h-5 rounded-full bg-iflow-100 text-iflow-600 flex items-center justify-center text-[10px] font-medium flex-shrink-0">
                        {granteeInitial(perm)}
                      </div>
                      <span className="text-xs text-gray-700 truncate">{granteeLabel(perm)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPermModalCollaborator({ grantee_id: perm.grantee_id, grantee_email: perm.grantee_email || granteeLabel(perm) })}
                        className="text-[10px] text-iflow-500 capitalize underline decoration-dotted underline-offset-2 hover:text-iflow-600 hover:decoration-solid cursor-pointer transition-colors"
                        title="Click to edit permissions"
                      >
                        {perm.permission_level}
                      </button>
                      {isManager && (
                        <button
                          onClick={() => removeCollaboratorMutation.mutate(perm.grantee_id)}
                          className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover/collab:opacity-100 transition-opacity"
                          title="Remove"
                        >
                          <span className="text-xs">&times;</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Inherited from project */}
                {inheritedCollaborators.map((perm: Permission) => (
                  <div key={`inh-${perm.grantee_id}`} className="flex items-center justify-between opacity-60" title="Inherited from project">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-[10px] font-medium flex-shrink-0">
                        {granteeInitial(perm)}
                      </div>
                      <span className="text-xs text-gray-500 truncate">{granteeLabel(perm)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPermModalCollaborator({ grantee_id: perm.grantee_id, grantee_email: perm.grantee_email || granteeLabel(perm) })}
                        className="text-[10px] text-iflow-500 capitalize underline decoration-dotted underline-offset-2 hover:text-iflow-600 hover:decoration-solid cursor-pointer transition-colors"
                        title="Click to edit permissions"
                      >
                        {perm.permission_level}
                      </button>
                      <span className="text-[9px] text-gray-300 italic">inherited</span>
                    </div>
                  </div>
                ))}

                {!folderCollaborators?.length && !inheritedCollaborators.length && (
                  <p className="text-xs text-gray-500">No collaborators yet.</p>
                )}

                {isManager && (
                  <button
                    onClick={() => setShowShareFolder(true)}
                    className="text-xs text-iflow-600 hover:text-iflow-700 font-medium mt-1"
                  >
                    + Add more
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Document list with drop zone */}
        <div
          className="flex-1"
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node))
              setDragOver(false)
          }}
          onDrop={handleInlineDrop}
        >
          {/* Drop zone overlay */}
          {dragOver && (
            <div className="border-2 border-dashed border-iflow-400 bg-white/30 rounded-xl p-8 mb-4 text-center pointer-events-none">
              <FileUp size={32} className="mx-auto text-iflow-400 mb-2" />
              <p className="text-sm text-iflow-600 font-medium">
                Drop files to upload to &ldquo;{currentFolder?.name || 'this folder'}&rdquo;
              </p>
            </div>
          )}

          {/* Inline upload progress */}
          {inlineFiles.length > 0 && (
            <div className="mb-4 space-y-2">
              {inlineFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-3 light-card p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{f.file.name}</p>
                    <p className="text-xs text-gray-500">{(f.file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  {f.status === 'uploading' && (
                    <div className="w-24">
                      <div className="h-1.5 bg-sky-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-iflow-500 rounded-full transition-all"
                          style={{ width: `${f.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {f.status === 'complete' && <CheckCircle size={18} className="text-green-500 flex-shrink-0" />}
                  {f.status === 'error' && (
                    <span className="flex items-center gap-1 text-xs text-red-500 flex-shrink-0">
                      <AlertCircle size={14} /> {f.error}
                    </span>
                  )}
                  {(f.status === 'complete' || f.status === 'error') && (
                    <button
                      onClick={() => setInlineFiles((prev) => prev.filter((pf) => pf.file !== f.file))}
                      className="text-gray-500 hover:text-gray-600 flex-shrink-0"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Document count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">{documents?.length || 0} document(s)</p>
            <div className="flex items-center border border-sky-200 rounded-lg overflow-hidden">
              <button
                onClick={() => { setDocView('list'); try { localStorage.setItem('innovateflow:doc-view:folder', 'list') } catch { /* a browser with site data blocked keeps its default view */ } }}
                className={`p-1.5 ${docView === 'list' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                title="List view" aria-pressed={docView === 'list'}
              ><List size={14} /></button>
              <button
                onClick={() => { setDocView('grid'); try { localStorage.setItem('innovateflow:doc-view:folder', 'grid') } catch { /* a browser with site data blocked keeps its default view */ } }}
                className={`p-1.5 ${docView === 'grid' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                title="Grid view" aria-pressed={docView === 'grid'}
              ><LayoutGrid size={14} /></button>
            </div>
          </div>

          {/* Bulk actions bar */}
          {selectedDocs.size > 0 && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 light-card">
              <span className="text-sm text-iflow-700 font-medium">{selectedDocs.size} selected</span>
              <button
                onClick={() => { setDeleteError(''); setShowDeleteConfirm(true) }}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-red-600 px-2 py-1 rounded hover:bg-white"
              >
                <Trash2 size={14} /> Delete
              </button>
              <button
                onClick={() => setShowBulkMove(true)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-iflow-600 px-2 py-1 rounded hover:bg-white"
              >
                <MoveRight size={14} /> Move
              </button>
              <button
                onClick={() => setSelectedDocs(new Set())}
                className="ml-auto text-sm text-gray-500 hover:text-gray-700"
              >
                Clear selection
              </button>
            </div>
          )}

          {!documents?.length && !inlineFiles.length ? (
            <EmptyState
              icon={Upload}
              title="This folder is empty"
              description="Drag & drop files here, or use Add Files to upload the first document."
              action={
                <label className="inline-flex items-center gap-1.5 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800 cursor-pointer">
                  <FileUp size={14} /> Add Files
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => e.target.files && handleInlineFileInput(e.target.files)}
                  />
                </label>
              }
            />
          ) : docView === 'grid' ? (
            <DocumentGrid documents={documents || []} />
          ) : (
            <div className="space-y-3">
              {documents?.map((doc) => (
                <div key={doc.document_id} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedDocs.has(doc.document_id)}
                    onChange={() => toggleSelect(doc.document_id)}
                    className="w-4 h-4 text-iflow-500 border-gray-300 rounded focus:ring-iflow-500"
                  />
                  <div className="flex-1">
                    <DocumentCard document={doc} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Folder tabs — Journals, Contacts, Orgs, Notes, Connections */}
          {projectId && folderId && (
            <FolderListTabs
              projectId={projectId}
              folderId={folderId}
              selectedDocIds={Array.from(selectedDocs)}
            >
              {(activeTab: FolderTab) => {
                switch (activeTab) {
                  case 'journals':
                    return <RelatedJournalsPanel projectId={projectId} folderId={folderId} />
                  case 'collaboration':
                    return <CollaborationPanel scope={{ type: 'folder', projectId, folderId }} />
                  case 'notes':
                    return <FolderNotesList projectId={projectId} folderId={folderId} />
                  case 'connections':
                    return <FolderConnectionsGraph projectId={projectId} folderId={folderId} selectedDocIds={Array.from(selectedDocs)} />
                  case 'ai-analysis':
                    return <FolderAIAnalysisPanel projectId={projectId} folderId={folderId} />
                  case 'prior-art':
                    return <FolderPriorArtPanel projectId={projectId} folderId={folderId} />
                  default:
                    return null
                }
              }}
            </FolderListTabs>
          )}
        </div>
      </div>

      {/* Bulk Move Modal */}
      {showBulkMove && projectId && (
        <MoveDocumentModal
          documentIds={Array.from(selectedDocs)}
          currentFolderId={folderId || null}
          projectId={projectId}
          onClose={() => {
            setShowBulkMove(false)
            setSelectedDocs(new Set())
          }}
        />
      )}

      {/* Share Folder Modal */}
      {showShareFolder && projectId && folderId && (
        <ShareModal
          resourceId={folderId}
          resourceType="folder"
          projectId={projectId}
          onClose={() => {
            setShowShareFolder(false)
            queryClient.invalidateQueries({ queryKey: ['permissions', folderId] })
          }}
        />
      )}

      {/* Collaborator Permissions Modal */}
      <CollaboratorPermissionsModal
        isOpen={!!permModalCollaborator}
        onClose={() => setPermModalCollaborator(null)}
        resourceId={folderId!}
        resourceType="folder"
        resourceName={currentFolder?.name || 'Folder'}
        granteeId={permModalCollaborator?.grantee_id || ''}
        granteeEmail={permModalCollaborator?.grantee_email || ''}
        isManager={isManager}
        onUpdated={() => {
          queryClient.invalidateQueries({ queryKey: ['permissions', folderId] })
          queryClient.invalidateQueries({ queryKey: ['permissions', projectId] })
        }}
      />

      {/* Delete Documents Confirm */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete Documents"
        message={`Are you sure you want to delete ${selectedDocs.size} document(s)? This will also remove their embeddings and cannot be undone.${deleteError ? `\n\nError: ${deleteError}` : ''}`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteDocsMutation.mutate(Array.from(selectedDocs))}
        onCancel={() => { setShowDeleteConfirm(false); setDeleteError('') }}
      />

      {/* Delete Folder Confirm */}
      <ConfirmDialog
        open={!!deleteFolderTarget}
        title="Delete Folder"
        message={`Are you sure you want to delete "${deleteFolderTarget?.name}"? The folder must be empty.${deleteFolderError ? `\n\nError: ${deleteFolderError}` : ''}`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteFolderTarget && deleteFolderMutation.mutate(deleteFolderTarget.folder_id)}
        onCancel={() => { setDeleteFolderTarget(null); setDeleteFolderError('') }}
      />

      {/* Create Subfolder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowCreateFolder(false); setCreateFolderError('') }} />
          <div className="relative bg-[#0c4a6e] rounded-xl border border-white/20 backdrop-blur-md shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Create Subfolder</h3>
            <p className="text-sm text-white/60 mb-3">
              Inside: <span className="font-medium text-white/80">{currentFolder?.name || 'folder'}</span>
            </p>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">Folder Name</label>
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' &&
                  newFolderName.trim() &&
                  createFolderMutation.mutate({ name: newFolderName.trim(), parent_folder_id: folderId || null })
                }
                className="w-full px-3 py-2 glass-input text-sm outline-none"
                placeholder="e.g. Patent Drafts"
                autoFocus
              />
            </div>
            {createFolderError && (
              <p className="text-sm text-red-300 mt-3">{createFolderError}</p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowCreateFolder(false); setCreateFolderError('') }}
                className="px-4 py-2 text-sm text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  createFolderMutation.mutate({ name: newFolderName.trim(), parent_folder_id: folderId || null })
                }
                disabled={!newFolderName.trim() || createFolderMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                {createFolderMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
